import { SharedQueryParamKey } from '@ddt-web/url';
import * as get from 'lodash/get';
import * as set from 'lodash/set';

import { ToolNameConfig } from '../configs';
import { TealiumConstants } from '../constants/tealium.constants';
import { TealiumToolState } from '../models/tealium-tool-state.enum';

export class GlobalAnalyticsMapperUtil {
	public static setMode(): void {
		const qsSearchParams = new URLSearchParams(location.search);
		const mode = qsSearchParams && qsSearchParams.has(SharedQueryParamKey.ENGAGEMENT) ? 'portfolio solution view' : 'self service view';

		set(window, 'globalAnalyticsParams.mode', mode);
	}

	public static getMode(): string {
		return get(window, 'globalAnalyticsParams.mode');
	}

	public static setToolName(): void {
		const configItem = ToolNameConfig.find(config => window.location.href.match(config.id) != null);
		if (configItem) {
			set(window, 'globalAnalyticsParams.toolName', configItem.toolName);
		}
	}

	public static getToolName(): string {
		return get(window, 'globalAnalyticsParams.toolName');
	}

	public static setTrackingVariable(): void {
		const trackingVariable = window[TealiumConstants.DATA_MAPPING.NT_PT_GLOBAL_EXTRA];
		set(window, 'globalAnalyticsParams.trackingVariable', trackingVariable);
	}

	/**
	 * This is only for SSO partners
	 */
	public static setPartnerName(partner: string): void {
		set(window, 'globalAnalyticsParams.partnerName', partner);
	}

	public static getPartnerName(): string {
		return get(window, 'globalAnalyticsParams.partnerName');
	}

	/**
	 * This is only for import partners
	 * @param partner Optional so that it can be cleared
	 */
	public static setImportPartnerName(partner?: string): void {
		set(window, 'globalAnalyticsParams.importPartnerName', partner);
	}

	public static getImportPartnerName(): string {
		return get(window, 'globalAnalyticsParams.importPartnerName');
	}

	// this is specific to partner partfolios e.g. APM, UMA
	public static setPartnerPortfolioType(type: string): void {
		set(window, 'globalAnalyticsParams.partnerPortfolioType', type);
	}

	public static getPartnerPortfolioType(): string {
		return get(window, 'globalAnalyticsParams.partnerPortfolioType');
	}

	public static setSelectedPortfolioPartner(partner: string): void {
		set(window, 'globalAnalyticsParams.selectedPortfolioPartner', partner);
	}

	public static setToolState(toolState: TealiumToolState): void {
		set(window, 'globalAnalyticsParams.toolState', toolState);
	}

	public static getToolState(): string {
		return get(window, 'globalAnalyticsParams.toolState');
	}

	public static getPortfolioIds(): string {
		return get(window, 'globalAnalyticsParams.portfolioIds');
	}

	public static setPortfolioIds(portfolioIds: string): void {
		set(window, 'globalAnalyticsParams.portfolioIds', portfolioIds);
	}

	public static setToolBlkModelPortfolio(blkModelPortfolio: string): void {
		set(window, 'globalAnalyticsParams.blkModelPortfolio', blkModelPortfolio);
	}

	public static setToolThirdPartyModelPortfolio(thirdPartyModelPortfolio: string): void {
		set(window, 'globalAnalyticsParams.thirdPartyModelPortfolio', thirdPartyModelPortfolio);
	}

	public static setToolMySavedPortfolio(mySavedPortfolio: string): void {
		set(window, 'globalAnalyticsParams.mySavedPortfolio', mySavedPortfolio);
	}

	public static setToolHoldingNum(holdingNum: string): void {
		set(window, 'globalAnalyticsParams.holdingNum', holdingNum);
	}

	public static setInternalUser(internalUser: string): void {
		set(window, 'globalAnalyticsParams.internalUser', internalUser);
	}

	/**
	 * @deprecated update unica tags in teamsite for the page
	 * See: https://webster.bfm.com/Wiki/pages/viewpage.action?pageId=660903998
	 */
	public static setSiteName(siteName: string): void {
		set(window, 'globalAnalyticsParams.siteName', siteName);
	}

	/**
	 * @deprecated update unica tags in teamsite for the page
	 * See: https://webster.bfm.com/Wiki/pages/viewpage.action?pageId=660903998
	 */
	public static setSiteSection(siteSection: string): void {
		set(window, 'globalAnalyticsParams.siteSection', siteSection);
	}

	/**
	 * @deprecated update unica tags in teamsite for the page
	 * See: https://webster.bfm.com/Wiki/pages/viewpage.action?pageId=660903998
	 */
	public static setSiteSubSection(siteSubSection: string): void {
		set(window, 'globalAnalyticsParams.siteSubSection', siteSubSection);
	}

	/**
	 * @deprecated update unica tags in teamsite for the page
	 * See: https://webster.bfm.com/Wiki/pages/viewpage.action?pageId=660903998
	 */
	public static setSiteSubSubSection(siteSubSubSection: string): void {
		set(window, 'globalAnalyticsParams.siteSubSubSection', siteSubSubSection);
	}

	/**
	 * @deprecated update unica tags in teamsite for the page
	 * See: https://webster.bfm.com/Wiki/pages/viewpage.action?pageId=660903998
	 */
	public static setPageName(pageName: string): void {
		set(window, 'globalAnalyticsParams.pageName', pageName);
	}
	/**
	 * @deprecated update unica tags in teamsite for the page
	 * See: https://webster.bfm.com/Wiki/pages/viewpage.action?pageId=660903998
	 */
	public static setEngagementId(engagementId: string): void {
		set(window, 'globalAnalyticsParams.engagementId', engagementId);
	}


	public static setPortfolioNames(portfolioNames: string): void {
		set(window, 'globalAnalyticsParams.portfolioNames', portfolioNames);
	}

	// ===================== CMA Estimator / Expected Return Analyzer =====================

	public static getCmaGoal(): string {
		return get(window, 'globalAnalyticsParams.cmaGoal');
	}
	public static setCmaGoal(cmaGoal: string): void {
		set(window, 's.eVar114', cmaGoal);
		set(window, 'globalAnalyticsParams.cmaGoal', cmaGoal);
	}

	public static getCmaStrategyOptions(): string {
		return get(window, 'globalAnalyticsParams.cmaStrategyOptions');
	}
	public static setCmaStrategyOptions(cmaStrategyOptions: string): void {
		set(window, 's.eVar116', cmaStrategyOptions);
		set(window, 'globalAnalyticsParams.cmaStrategyOptions', cmaStrategyOptions);
	}

	public static getCmaStrategy(): string {
		return get(window, 'globalAnalyticsParams.cmaStrategy');
	}
	public static setCmaStrategy(cmaStrategy: string): void {
		set(window, 's.eVar115', cmaStrategy);
		set(window, 'globalAnalyticsParams.cmaStrategy', cmaStrategy);
	}

	public static getCmaPortfolioIsEdited(): string {
		return get(window, 'globalAnalyticsParams.isEdited');
	}
	public static setCmaPortfolioIsEdited(isEdited: boolean): void {
		set(window, 'globalAnalyticsParams.cmaPortfolioIsEdited', String(isEdited));
	}

	// =============================== Portfolio Centre ===============================
	public static setCurrency(currency: string): void {
		set(window, 'globalAnalyticsParams.currency', currency);
	}

	public static setBenchmark(benchmark: string): void {
		set(window, 'globalAnalyticsParams.benchmark', benchmark);
	}

}
